// The photo editors' recent-photos card (the AI photo editor's look, shared by the Studio Editor and AI Templates): an orange-barred
// title, ‹ › buttons and a "view all" link, over a smooth Embla drag strip. Background jobs still running are pinned to the front;
// `hasMore` + `onLoadMore` page in older photos as the strip nears its end. Tiles take each photo's own shape (measured on load).
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IconChevronLeft, IconChevronRight, IconExternalLink, IconLoader2, IconX } from '@tabler/icons-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import Carousel from './EmblaCarousel';

const ARROW_CLS = 'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-zinc-500 text-white transition-colors hover:bg-zinc-600 disabled:opacity-40';

/**
 * @param history        [{ id, aiImage, fileName? }]
 * @param bgJobs         [{ token, status: 'processing'|'error', previewUrl, restored? }]
 * @param viewAllTo      the "view all" link (My Files tab)
 * @param tileH          tile height in px (width follows the photo's shape)
 * @param hasMore / loadingMore / onLoadMore   paging; loads when the strip is 75% scrolled or can't scroll further
 */
export default function RecentPhotosStrip({
    title, emptyText, history = [], historyLoading = false, onOpenHistory,
    bgJobs = [], onOpenJob, onDismissJob,
    viewAllTo, tileH = 144, hasMore = false, loadingMore = false, onLoadMore, className,
}) {
    const { t } = useTranslation();
    const [embla, setEmbla] = useState(null);
    const [ratios, setRatios] = useState({});

    // New slides (a page of history, a job) → let Embla re-measure
    useEffect(() => { embla?.reInit(); }, [embla, history.length, bgJobs.length, hasMore]);

    useEffect(() => {
        if (!embla || !onLoadMore) return undefined;
        const check = () => {
            if (!hasMore || loadingMore) return;
            if (embla.scrollProgress() >= 0.75 || !embla.canScrollNext()) onLoadMore();
        };
        embla.on('scroll', check);
        embla.on('settle', check);
        return () => { embla.off('scroll', check); embla.off('settle', check); };
    }, [embla, hasMore, loadingMore, onLoadMore]);

    const jobW = Math.round(tileH * 0.75);
    const tileW = (id) => Math.round(tileH * Math.min(1.8, Math.max(0.5, ratios[id] || 0.75)));
    const empty = history.length === 0 && bgJobs.length === 0;

    return (
        <div className={cn('rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900', className)}>
            <div className="mb-3 flex flex-wrap items-center gap-2">
                <span className="h-4 w-1 shrink-0 rounded-full bg-primary" />
                <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{title}</h3>
                {!empty && (
                    <div className="ml-auto flex items-center gap-1.5">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button type="button" onClick={() => embla?.scrollPrev()} className={ARROW_CLS} aria-label={t('imageGeneration.tooltipPrev')}><IconChevronLeft size={16} /></button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.tooltipPrev')}</TooltipContent>
                        </Tooltip>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button type="button" onClick={() => embla?.scrollNext()} className={ARROW_CLS} aria-label={t('imageGeneration.tooltipNext')}><IconChevronRight size={16} /></button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="z-[2000]">{t('imageGeneration.tooltipNext')}</TooltipContent>
                        </Tooltip>
                        {viewAllTo && (
                            <Link to={viewAllTo} className="inline-flex h-7 shrink-0 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-semibold text-white hover:bg-primary/90">
                                <IconExternalLink size={14} />
                                {t('imageGeneration.viewAllImages')}
                            </Link>
                        )}
                    </div>
                )}
            </div>
            {empty ? (
                <p className="py-6 text-center text-sm text-zinc-400 dark:text-zinc-500">{historyLoading ? t('common.loading') : emptyText}</p>
            ) : (
                <Carousel getEmblaApi={setEmbla} dragFree slideGap={12} align="start" loop={false} styles={{ viewport: { paddingTop: 6, marginTop: -6, paddingBottom: 12, marginBottom: -12 } }}>
                    {/* Edits still running in the background — pinned to the front */}
                    {bgJobs.map((job) => {
                        const live = job.status === 'processing' && !job.restored;
                        return (
                            <Carousel.Slide key={`job-${job.token}`}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            role={live ? 'button' : undefined}
                                            tabIndex={live ? 0 : undefined}
                                            onClick={() => live && onOpenJob?.(job)}
                                            onKeyDown={(e) => { if (live && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onOpenJob?.(job); } }}
                                            className={cn('relative overflow-hidden rounded-lg border-2 bg-zinc-800', job.status === 'error' ? 'border-red-400' : 'border-primary', live && 'cursor-pointer')}
                                            style={{ width: jobW, height: tileH }}
                                        >
                                            {job.previewUrl && <img src={job.previewUrl} alt="" draggable="false" className="h-full w-full object-cover opacity-60" />}
                                            {job.status === 'processing' ? (
                                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/35 text-white">
                                                    <IconLoader2 size={22} className="animate-spin" />
                                                    <span className="text-[11px] font-semibold !text-white">{t('imageGeneration.processing')}</span>
                                                </div>
                                            ) : (
                                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-red-600/60 text-white">
                                                    <button type="button" aria-label={t('common.close')} onClick={(e) => { e.stopPropagation(); onDismissJob?.(job); }} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/55 text-white">
                                                        <IconX size={12} />
                                                    </button>
                                                    <span className="text-[11px] font-semibold !text-white">{t('imageGeneration.failed')}</span>
                                                </div>
                                            )}
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="top" className="z-[2000]">
                                        {job.status === 'error' ? t('imageGeneration.notifications.backgroundImageFailed') : job.restored ? t('imageGeneration.processingRestored') : t('imageGeneration.processingInBackground')}
                                    </TooltipContent>
                                </Tooltip>
                            </Carousel.Slide>
                        );
                    })}
                    {history.map((img) => {
                        const ratio = ratios[img.id];
                        const tile = (
                            <button
                                type="button"
                                onClick={() => onOpenHistory?.(img)}
                                className="relative block overflow-hidden rounded-lg border border-zinc-200 bg-zinc-100 transition duration-200 hover:-translate-y-0.5 hover:border-primary hover:shadow-md dark:border-zinc-700 dark:bg-zinc-800"
                                style={{ width: tileW(img.id), height: tileH }}
                            >
                                {!ratio && <span className="absolute inset-0 animate-pulse bg-zinc-200 dark:bg-zinc-700" />}
                                <img
                                    src={img.aiImage}
                                    alt=""
                                    loading="lazy"
                                    draggable="false"
                                    onLoad={(e) => {
                                        const r = e.currentTarget.naturalWidth / e.currentTarget.naturalHeight;
                                        if (r) setRatios((prev) => (prev[img.id] ? prev : { ...prev, [img.id]: r }));
                                    }}
                                    className={cn('relative h-full w-full transition-opacity duration-300', ratio ? 'opacity-100' : 'opacity-0', ratio && (ratio > 1.8 || ratio < 0.5) ? 'object-contain' : 'object-cover')}
                                />
                            </button>
                        );
                        return (
                            <Carousel.Slide key={img.id}>
                                {img.fileName ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild>{tile}</TooltipTrigger>
                                        <TooltipContent side="top" className="z-[2000]">{img.fileName}</TooltipContent>
                                    </Tooltip>
                                ) : tile}
                            </Carousel.Slide>
                        );
                    })}
                    {/* More pages: a spinner while the next one loads */}
                    {hasMore && (
                        <Carousel.Slide>
                            <div className="flex w-16 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800" style={{ height: tileH }}>
                                {loadingMore && <IconLoader2 size={18} className="animate-spin text-zinc-400" />}
                            </div>
                        </Carousel.Slide>
                    )}
                </Carousel>
            )}
        </div>
    );
}
